import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ORIGIN_DOT_COLORS } from "@/data";
import { calculateBarPosition } from "@/utils/calculateBookingBarPosition";
import { formatISOToDDMMYYYY } from "@/utils/dateFormatter";
import { parseDateFromDatabase } from "@/utils/dateParser";
import { getBookingColor } from "@/utils/getBookingColor";

interface Booking {
  id: string;
  guest_name: string;
  check_in: string;
  check_out: string;
  nights: number;
  note?: string;
  payment_status: 'pending' | 'partial' | 'paid';
  origin_name?: string;
}

interface BookingBarProps {
  booking: Booking;
  days: Date[];
  cellWidth: number;
  rowHeight: number;
  visibleRange: { start: number; end: number };
  onClick?: (booking: Booking) => void;
}

const BookingBar = ({
  booking,
  days,
  cellWidth,
  rowHeight,
  visibleRange,
  onClick,
}: BookingBarProps) => {
  const checkIn = parseDateFromDatabase(booking.check_in);
  const checkOut = parseDateFromDatabase(booking.check_out);
  const originNameRaw = booking.origin_name || "";
  const originDotColor =
    ORIGIN_DOT_COLORS[originNameRaw.toLowerCase()] ?? ORIGIN_DOT_COLORS.default;

  if (!checkIn) return null;

  const bar = calculateBarPosition(
    checkIn,
    checkOut,
    days,
    booking.nights,
    cellWidth,
  );
  if (!bar) return null;

  const isVisible =
    bar.left + bar.width >= visibleRange.start && bar.left <= visibleRange.end;

  if (!isVisible) return null;

  const colorClass = getBookingColor(originNameRaw);
  const hasNote = !!booking.note?.trim();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          title={`${booking.guest_name} – ${booking.check_in} a ${booking.check_out}`}
          className={`absolute top-1/2 -translate-y-1/2 ${colorClass} text-black flex items-center transition-all px-2 cursor-pointer`}
          style={{
            width: bar.width,
            left: `${bar.left}px`,
            height: rowHeight - 25,
            fontSize: 10,
            borderRadius: 24,
          }}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.(booking);
          }}
        >
          {hasNote && (
            <div
              title="Esta reservación tiene notas"
              className={`w-2 h-2 rounded-full mr-1 flex-shrink-0 ${
                originDotColor || "bg-gray-500"
              }`}
            />
          )}

          <p className="truncate font-medium">{booking.guest_name}</p>
        </div>
      </TooltipTrigger>

      <TooltipContent>
        <div className="mb-2">
          <span
            className={`inline-block px-2 py-1 text-xs rounded-full ${
              booking.payment_status === 'paid'
                ? "bg-green-100 text-green-800"
                : booking.payment_status === 'partial'
                ? "bg-amber-100 text-amber-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {booking.payment_status === 'paid' ? "Pagado" : booking.payment_status === 'partial' ? "Pago parcial" : "Pendiente"}
          </span>
        </div>

        <p className="font-bold">
          {booking.guest_name.split(" ").slice(0, 3).join(" ")}
        </p>
        <p>Checkin: {formatISOToDDMMYYYY(booking.check_in)}</p>
        <p>Checkout: {formatISOToDDMMYYYY(booking.check_out)}</p>
        <p>Noches: {booking.nights}</p>

        {originNameRaw && (
          <p>
            Origen: <span className="capitalize">{originNameRaw}</span>
          </p>
        )}

        {hasNote && (
          <div className="mt-2 pt-2 border-t">
            <p className="font-semibold">Nota:</p>
            <p className="text-sm max-w-xs whitespace-pre-wrap">
              {booking.note}
            </p>
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
};

export default BookingBar;
